import BaseWrapper from "../../components/BaseWrapper/BaseWrapper";
import { useEffect, useState } from "react";
import { FormProvider } from "../../contexts/CreateRecordFormContext";
import RecordModal from "../../components/RecordModal/RecordModal";
import Feedback from "../../components/Feedback/Feedback";
import CustomersColumns from "../../lib/TableColumns/CustomersColumns"
import { AddCircle, Construction } from "@mui/icons-material";
import CustomersConfig from "../../lib/config/customers";
import { Box, CircularProgress } from "@mui/material";
import { fetchCustomersAndUsers } from "../../hooks/useCustomerActions";
import { useCustomerActions } from "../../hooks/useCustomerActions";
import CollapsibleGridCard from "../../components/CollapsibleGridCard";
import { useAuth } from "../../contexts/AuthContext";
import { GRID_SESSION_KEYS, readGridSessionState } from "../../lib/gridSessionState";

const Customers = () => {
    const [customers, setCustomers] = useState([]) 
    const [selectedRows, setSelectedRows] = useState([]);
    const [openCreateModal, setOpenCreateModal] = useState(false);
    const [openUpdateModal, setOpenUpdateModal] = useState(false);
    const [feedback, setFeedback] = useState({open: false, message: "", type: ""}) 
    const [users, setUsers] = useState([])
    const [loading, setLoading] = useState(true);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const { user } = useAuth();
    const sessionUserId = user?.userId || localStorage.getItem("id");

    // -- Columns saved in lib/TableColumns for readability -- //
    const columns = CustomersColumns()

    // -- Helper Function to update customers state after creation -- //
    const addCustomer = (newCustomer) => setCustomers(prevCustomers => [newCustomer, ...prevCustomers]);

    // -- Keep track of selected rows -- //
    const handleSelectionChange = (selection) => setSelectedRows(selection)

    // -- State Management for Modal that creates a new customer -- //
    const handleOpenCreateModal = () => setOpenCreateModal(true);
    const handleCloseCreateModal = () => setOpenCreateModal(false);

    // -- State Management for Modal that updates a existing customer(s) -- //
    const handleOpenUpdateModal = () => selectedRows.length ? setOpenUpdateModal(true) : setFeedback({open: true, message: "Select at least one record to update!", type: "info"})
    const handleCloseUpdateModal = () => setOpenUpdateModal(false);

    // -- Imported API Calls -- //
    const {
        handleCreateSubmit,
        handleUpdateSubmit,
        handleDeleteSubmit
    } = useCustomerActions({
        setCustomers,
        setFeedback,
        addCustomer,
        setSelectedRows,
        handleCloseCreateModal,
        handleCloseUpdateModal
    });

    // -- Save Grid Filters to be Default -- //

    // -- Define Buttons For Customers Component -- //
    const actionButtons = [
        { title: "Create Customer", onClick: handleOpenCreateModal, icon: AddCircle, color: "primary" },
        { title: "Update Selected Record(s)", onClick: handleOpenUpdateModal, icon: Construction, color: "primary" },
    ];

    // -- Available Fields When Updating a Customer -- //
    const updateCustomerFields = [
        { name: "owner", label: "Owner", type: 'autocomplete', options: users, getOptionLabel: (option) => option.label },
        { name: "organizationName", label: "Organization Name", type: "text" },
        { name: "address", label: "Address", type: "text" },
        { name: "city", label: "City", type: "text" },
        { name: "state", label: "State", type: "text" },
        { name: "province", label: "Province", type: "text" },
        { name: "countryCode", label: "Country", type: "text" },
        { name: "status", label: "Status", type: 'autocomplete',  options: ['New', 'Active', 'Inactive', 'Pending', 'Dormant', 'Churned']},
    ]  

    // -- Load Customers & Users When Page Renders -- //
    useEffect(() => {
        async function loadData() {
            try {
                const { customers, users } = await fetchCustomersAndUsers();
                const sessionGridState = readGridSessionState(GRID_SESSION_KEYS.customers, sessionUserId)
                setCustomers(customers);
                setUsers(users);
                setFilterModel(sessionGridState?.filterModel || { items: [] })
            } catch (error) {
                setFeedback(prev => ({...prev, open: true, message: 'Error: Failed To Load Customers...', type: 'error' }));
            } finally {
                setLoading(false);
            }
        }

        loadData()
    }, [sessionUserId])

    return (
        <BaseWrapper content={
            <>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <FormProvider>
                        <Feedback props={feedback} setFeedback={setFeedback} />
                        <RecordModal open={openCreateModal} handleClose={handleCloseCreateModal} fields={CustomersConfig.NewCustomerFields} title="Create New Customer" onSubmit={handleCreateSubmit} />
                        <RecordModal
                            open={openUpdateModal}
                            handleClose={handleCloseUpdateModal}
                            fields={updateCustomerFields}
                            title="Update Customer(s)"
                            onSubmit={(data) => handleUpdateSubmit(data, selectedRows)}
                            onDelete={() => handleDeleteSubmit(selectedRows)}
                            isUpdate={true}
                        />
                        <CollapsibleGridCard
                            title="Customers"
                            linkLabel="Organizations"
                            rows={customers}
                            columns={columns}
                            getRowId={(row) => row._id}
                            checkboxSelection
                            rowSelectionModel={selectedRows}
                            onRowSelectionModelChange={handleSelectionChange}
                            actionButtons={actionButtons}
                            sessionStorageKey={GRID_SESSION_KEYS.customers}
                            sessionUserId={sessionUserId}
                            initialPageSize={25}
                            pageSizeOptions={[25, 50, 100, 250]}
                            extraGridProps={{
                                onFilterModelChange: setFilterModel,
                                filterModel,
                            }}
                        />
                    </FormProvider>    
                )}   
            </>             
        } />
    )
}

export default Customers
